const API_BASE_URL = '/api';

export type PaymentsSortField = 'createdAt' | 'updatedAt' | 'amount';

// Mirrors PaymentService._map_to_payment_item on the backend. Not yet in
// the shared @techgitter/contracts package — promote to a contract once
// the shape stabilises and any other consumer needs it.
export interface PaymentsListItem {
  id: string | null;
  rsId: string | null;
  status: string | null;
  amount: number | null;
  refNum: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  customerName: string;
  customerDesignation: string | null;
  customerId: string | null;
  rsCustomerId: string | null;
  invoiceNumber: string | null;
  /** Invoice name (cr001_name). Requires the payments list endpoint to expand
   *  the linked invoice and include it; null until the backend provides it. */
  invoiceName?: string | null;
  rsInvoiceId: string | null;
  paymentMethod: string | null;
  userFullName: string | null;
}

export interface PaymentsListResponse {
  items: PaymentsListItem[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

export interface FetchPaymentsParams {
  page: number;
  pageSize: number;
  sort?: PaymentsSortField;
  order?: 'asc' | 'desc';
  search?: string;
  paymentMethod?: string;
  invoiceNumber?: string;
  createdFrom?: string;
  createdTo?: string;
  includeDeleted?: boolean;
  signal?: AbortSignal;
}

export async function fetchPayments(
  params: FetchPaymentsParams,
): Promise<PaymentsListResponse> {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('pageSize', String(params.pageSize));
  if (params.sort) qs.set('sort', params.sort);
  if (params.order) qs.set('order', params.order);
  if (params.search) qs.set('search', params.search);
  if (params.paymentMethod) qs.set('paymentMethod', params.paymentMethod);
  if (params.invoiceNumber) qs.set('invoiceNumber', params.invoiceNumber);
  if (params.createdFrom) qs.set('createdFrom', params.createdFrom);
  if (params.createdTo) qs.set('createdTo', params.createdTo);
  if (params.includeDeleted) qs.set('includeDeleted', 'true');

  const response = await fetch(`${API_BASE_URL}/payments?${qs}`, {
    method: 'GET',
    headers: { 'Accept': 'application/json' },
    signal: params.signal,
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch payments: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  return {
    items: data.items ?? [],
    pagination: data.pagination ?? {
      page: 1,
      pageSize: 50,
      totalItems: 0,
      totalPages: 0,
      hasNext: false,
      hasPrevious: false,
    },
  };
}

// Maps to api/payments POST handler's body shape. Mirror of
// CreatePaymentRequest in @techgitter/contracts.
export interface CreatePaymentParams {
  rsInvoiceId: number;
  rsCustomerId: number;
  paymentMethodName: string;
  dvPaymentMethodId?: string;
  rsPaymentMethodId?: number;
  amount: number;
  refNum?: string;
  paymentDate?: string;            // YYYY-MM-DD; defaults server-side to today
}

export interface CreatePaymentResponse {
  messageId: string;
  status: string;
  invoiceNumber: string | null; // display number (cr001_number); null if unresolved
  invoiceId: string | null;     // DV GUID (cr001_invoicesid); null if unresolved
  amount: number;
}

export async function createPayment(
  params: CreatePaymentParams,
  signal?: AbortSignal,
): Promise<CreatePaymentResponse> {
  const response = await fetch(`${API_BASE_URL}/payments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
    signal,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => null);
    throw new Error(err?.error ?? `Failed to record payment: ${response.status}`);
  }
  return response.json();
}

